// Globals
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { selectProfile, selectProfileIsLoading, updateProfile } from '../../feature';

// Modules
import { ChangePasswordContainer } from '@/modules/Auth';
import { uploadCommonFile } from '@/modules/Upload';

// Components
import { EditableInput, FileUpload, Modal, ModalSize } from '@/components';
import { TrashIcon, UploadAvatarIcon } from '@/icons';
import { Button } from '@/ui';
import {
  StyledAvatar,
  StyledAvatarWrapper,
  StyledContainer,
  StyledDeleteButton,
  StyledHeaderBlock,
  StyledPageTitle,
  StyledUploadAvatar,
  StyledWrapper,
} from './ProfileContainerStyles';

// Helpers
import { useModal } from '@/hooks';
import { UpdateProfileDTO } from '../../dtos';
import { ACCEPT_IMAGE_FILE_TYPES, MAX_IMAGE_SIZE, ProfileFields, TProfileValues } from '../../helpers';
import { useValidationFormRules } from '../../hooks';

// Models
import { FileType } from '@/models/uploads.model';

const ProfileContainer = () => {
  const { t } = useTranslation('profile');
  const { toggleModal, isOpened } = useModal();

  const profile = useAppState(selectProfile);
  const isLoading = useAppState(selectProfileIsLoading);
  const dispatch = useAppDispatch();
  const rules = useValidationFormRules();

  const handleSubmit = (values: Partial<TProfileValues>) => {
    const dto = new UpdateProfileDTO(values);
    dispatch(updateProfile(dto));
  };

  const handleUploadAvatar = async (file: File) => {
    try {
      const uploadedFile = await dispatch(uploadCommonFile({ file, type: FileType.IMAGE })).unwrap();
      handleSubmit({ [ProfileFields.PHOTO]: uploadedFile?.asset_public_url });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteAvatar = () => handleSubmit({ [ProfileFields.PHOTO]: null });

  return (
    <StyledContainer>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('profile_title')}</StyledPageTitle>
      </StyledHeaderBlock>
      <StyledWrapper>
        <EditableInput
          initialValues={{ [ProfileFields.FULL_NAME]: profile?.full_name }}
          name={ProfileFields.FULL_NAME}
          onChange={handleSubmit}
          isLoading={isLoading}
          rules={rules[ProfileFields.FULL_NAME]}
          label={t('label_name')}
        />
      </StyledWrapper>

      <StyledWrapper>
        <EditableInput
          initialValues={{ [ProfileFields.EMAIL]: profile?.email }}
          name={ProfileFields.EMAIL}
          onChange={handleSubmit}
          isLoading={isLoading}
          rules={rules[ProfileFields.EMAIL]}
          label={t('label_email')}
        />
      </StyledWrapper>

      <StyledUploadAvatar>
        <StyledAvatarWrapper>
          {profile?.profile_photo && (
            <StyledDeleteButton danger loading={isLoading} onClick={handleDeleteAvatar} icon={<TrashIcon />} />
          )}
          <StyledAvatar src={profile?.profile_photo} name={profile?.full_name} alt={profile?.full_name} />
        </StyledAvatarWrapper>
        <FileUpload
          type="select"
          onChange={handleUploadAvatar}
          acceptance={ACCEPT_IMAGE_FILE_TYPES}
          maxFileSize={MAX_IMAGE_SIZE}
        >
          <Button type="default" loading={isLoading} shape="round" icon={<UploadAvatarIcon />}>
            {t('button_upload_photo')}
          </Button>
        </FileUpload>
      </StyledUploadAvatar>
      <div>
        <Button type="default" shape="round" onClick={toggleModal}>
          {t('button_change_password')}
        </Button>
        <Modal size={ModalSize.MEDIUM} open={isOpened} title={t('button_change_password')} onCancel={toggleModal}>
          <ChangePasswordContainer onSubmit={toggleModal} />
        </Modal>
      </div>
    </StyledContainer>
  );
};

export default ProfileContainer;
